import React, { useState } from 'react';
import {
  FEATURE_FLAG_CROSS_SELL,
  MAX_CROSS_SELL_DISPLAY_ITEMS,
  CrossSellContext
} from '../../../../../utils/cross-sell';
import { GetFlag } from '../../../../../utils/featureFlag';
import {
  BpAddress,
  BpCnpj,
  BpInfoContainer,
  BpName,
  CloseButton,
  Container,
  CrossSellOrientation,
  HeaderRow,
  ProductsContainer
} from './styles';
import { Icon, Skeleton, Title } from '@gerdau/hefesto';
import CrossSellItem from './CrossSellItem';
import LoadingModal from '../../../../../components/LoadingModal';
import { ApplicationState } from '../../../../../store';
import { useSelector } from 'react-redux';
import { useFilteredCrossSellItems } from '../../../../../services/MVP/useFilteredCrossSellItems';
import { SortProductsItem } from '../../../../../services/MVP/models';
import { cnpjMask } from '../../../../../utils/formatStrings';

const CrossSellLoadingItem = () => <Skeleton type="input" height={272} />;

interface CrossSellProductsProps {
  setCrossSellKey: React.Dispatch<React.SetStateAction<string>>;
  orientation?: CrossSellOrientation;
  filteredItems?: SortProductsItem[];
  orderSoldTo?: string;
  orderShipTo?: string;
  screenName?: string;
  onItemAdded?: () => void;
  syncLoading?: boolean;
  onDismiss?: () => void;
  showBpInfo?: boolean;
  title?: string;
  crossSellContext?: CrossSellContext;
}

export const CrossSellProducts = ({
  setCrossSellKey,
  orientation = 'horizontal',
  filteredItems,
  orderSoldTo,
  orderShipTo,
  screenName,
  onItemAdded,
  syncLoading,
  onDismiss,
  showBpInfo = false,
  title,
  crossSellContext
}: CrossSellProductsProps) => {
  const crossSellFeatureFlag = GetFlag(FEATURE_FLAG_CROSS_SELL);
  const crossSellTopFlag = GetFlag('cross-sell-top');

  const state = useSelector((appState: ApplicationState) => appState.crossSell);
  const customers = useSelector(
    (appState: ApplicationState) => appState.customers.customers
  );

  const [loading, setLoading] = useState(false);

  const filteredCrossSellItems = useFilteredCrossSellItems();
  const displayItems = filteredItems ?? filteredCrossSellItems;

  const selectedBpCustomer = state.selectedBusinessPartner
    ? customers.find((c) => c.customerId === state.selectedBusinessPartner)
    : undefined;

  if (!crossSellFeatureFlag) {
    return null;
  }

  if (!state.loading && displayItems.length === 0) {
    return null;
  }

  return (
    <Container crossSellTopFlag={crossSellTopFlag} orientation={orientation}>
      <LoadingModal
        enabled={loading}
        title="Aguarde alguns instantes"
        message="Adicionando o produto ao carrinho..."
      />
      <header>
        <HeaderRow>
          <Title level={3}>
            {showBpInfo && selectedBpCustomer ? 'Sugestões para:' : title ?? ''}
          </Title>

          {onDismiss && (
            <CloseButton onClick={onDismiss} aria-label="Fechar sugestões">
              <Icon option="X" size="sm" />
            </CloseButton>
          )}
        </HeaderRow>
        {showBpInfo && selectedBpCustomer && (
          <BpInfoContainer>
            <BpName>{selectedBpCustomer.name}</BpName>
            {selectedBpCustomer.locality && (
              <BpAddress>
                {selectedBpCustomer.locality.address ?? ''},{' '}
                {selectedBpCustomer.locality.city ?? ''}-
                {selectedBpCustomer.locality.state ?? ''}
              </BpAddress>
            )}
            {selectedBpCustomer.taxNumber && (
              <BpCnpj>CNPJ: {cnpjMask(selectedBpCustomer.taxNumber)}</BpCnpj>
            )}
          </BpInfoContainer>
        )}
      </header>
      <ProductsContainer orientation={orientation}>
        {state.loading
          ? Array.from({ length: MAX_CROSS_SELL_DISPLAY_ITEMS }).map(
              (_, index) => <CrossSellLoadingItem key={index} />
            )
          : displayItems
              .slice(0, MAX_CROSS_SELL_DISPLAY_ITEMS)
              .map((prd, index) => (
                <CrossSellItem
                  key={prd.sku}
                  index={index}
                  product={prd}
                  setCrossSellKey={setCrossSellKey}
                  setLoading={setLoading}
                  orderSoldTo={orderSoldTo}
                  orderShipTo={orderShipTo}
                  screenName={screenName}
                  onItemAdded={onItemAdded}
                  syncLoading={syncLoading}
                  crossSellContext={crossSellContext}
                />
              ))}
      </ProductsContainer>
    </Container>
  );
};
